/**
 * <Markdown body={...} /> — shared markdown renderer.
 *
 * Wires `react-markdown` + `remark-gfm` + `rehype-highlight` together
 * with the workspace-wide mention pill renderer from
 * `lib/mention-render.tsx` so any markdown surface (constitution,
 * notes, sub-tasks, comments, card instructions, templates, role
 * prompts) gets rich rendering AND @user / @role / @artifact pills in
 * one component.
 *
 * Why a separate component:
 *   - the chat panes already use a lazy `agent/markdown-content.tsx`;
 *     that one is heavy (highlight.js for every fenced block) and
 *     designed for streamed agent output. This one is the everyday
 *     UI renderer used inside dialogs / cards / settings panels.
 *   - mention parsing has to run *inside* the rendered tree, not on
 *     the raw string, otherwise markdown formatting would break the
 *     mention regex (e.g. `**@user:foo**` would stop matching).
 *
 * Mention integration strategy:
 *   - override react-markdown's `p`, `li`, `td`, `th`, `em`, `strong`
 *     renderers so any *child text node* is scanned for mention
 *     tokens via the shared `tokenize()` function and rewritten into
 *     a mix of plain strings + `<MentionPill>` elements.
 *   - text inside `code` / `pre` is left untouched (no pills inside
 *     code blocks).
 *
 * Safety:
 *   - react-markdown does NOT render raw HTML unless `rehype-raw` is
 *     added, so XSS through `<script>` / `<img onerror>` is blocked
 *     by default.
 *   - links: external http(s) open in a new tab via the shared
 *     `MarkdownLink` renderer (lib/markdown-link); internal / hash
 *     / mailto links stay in the same tab.
 *
 * Styling: relies on `@tailwindcss/typography` (added 2026-05-20).
 *   - default classes: `prose prose-sm dark:prose-invert max-w-none`
 *   - heading sizes shrunk via the `prose-headings:` modifier so
 *     they don't dwarf the surrounding panel chrome.
 *
 * Bundle strategy (2026-05-25 Safari-12 pass):
 *   - The heavy `react-markdown` + `remark-gfm` + `rehype-highlight`
 *     trio is now dynamic-imported via `React.lazy`. Vite still puts
 *     the imports into the `markdown-vendor` chunk (configured in
 *     vite.config.ts manualChunks), but the chunk now loads lazily —
 *     it is NOT in the initial bundle of any route that statically
 *     imports `<Markdown>`. On modern browsers users see a brief
 *     plain-text flash before the chunk resolves; on Safari 12 / iOS
 *     12 the chunk is NEVER fetched (it crashes those engines at
 *     parse time because highlight.js uses lookbehind regex).
 */
import * as React from 'react';

import { isLegacyRegexBrowser } from '../../lib/legacy-browser';
import { MentionPill, tokenize } from '../../lib/mention-render';
import { MarkdownLink } from '../../lib/markdown-link';
import { cn } from '../../lib/utils';

interface MarkdownProps {
  /** Raw markdown source. Empty/null renders a muted placeholder. */
  body: string | null | undefined;
  /** Extra Tailwind classes appended to the prose container. */
  className?: string;
  /** Override the placeholder shown when body is empty. */
  emptyText?: string;
  /**
   * If true, highlights code blocks via rehype-highlight (adds a
   * ~30 KB chunk). Defaults to true. Set false for tiny preview
   * surfaces where the perf budget matters.
   */
  highlight?: boolean;
}

/**
 * Replace mention tokens inside a plain string with a mix of strings
 * and `<MentionPill>` React nodes. Returns React children safe to
 * splice into a markdown leaf component.
 */
function renderMentions(text: string, keyPrefix: string): React.ReactNode {
  if (!text || !text.includes('@')) return text;
  const segments = tokenize(text);
  // No mentions found → return the plain string (cheap path).
  if (segments.length === 0 || segments.every((s) => s.kind === 'text')) {
    return text;
  }
  return segments.map((seg, i) => {
    if (seg.kind === 'text') {
      return <React.Fragment key={`${keyPrefix}-t-${i}`}>{seg.text}</React.Fragment>;
    }
    return <MentionPill key={`${keyPrefix}-m-${i}`} data={seg.data} />;
  });
}

/**
 * Recursively walk children of a markdown leaf, rewriting any string
 * descendant via `renderMentions`. Non-string children (already React
 * elements like <strong>, <em>, <a>, <code>) are passed through as-is
 * — react-markdown's own components handle them.
 *
 * We intentionally skip recursion into already-rendered <code> / <pre>
 * children because their text was emitted by rehype-highlight and
 * contains <span> wrappers we don't want to break.
 */
function walkChildren(children: React.ReactNode, keyPrefix: string): React.ReactNode {
  return React.Children.map(children, (child, idx) => {
    if (typeof child === 'string') {
      return renderMentions(child, `${keyPrefix}-${idx}`);
    }
    return child;
  });
}

/**
 * Dynamic-import boundary for the heavy markdown stack. Defined ONCE at
 * module scope (not inside `<Markdown>`) so React reuses the same lazy
 * component across renders, which means the import() promise resolves
 * once and never restarts the chunk fetch on every render.
 *
 * The `React.lazy` factory imports react-markdown + plugins in parallel,
 * then wraps them in a tiny renderer that consumes the same baseComponents
 * map used by the static implementation. The renderer is the lazy
 * component's `default` export so Suspense can render it.
 */
type RichRendererProps = {
  body: string;
  highlight: boolean;
};

const RichMarkdown = React.lazy(async () => {
  const [{ default: ReactMarkdown }, { default: remarkGfm }, { default: rehypeHighlight }] =
    await Promise.all([
      import('react-markdown'),
      import('remark-gfm'),
      import('rehype-highlight'),
    ]);

  // `Components` typed loosely via `Parameters<typeof ReactMarkdown>[0]`
  // would re-import the type; the override map shape has been stable
  // through three react-markdown majors, so the inline cast is fine.
  const components = {
    p: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <p {...rest}>{walkChildren(children, 'p')}</p>
    ),
    li: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <li {...rest}>{walkChildren(children, 'li')}</li>
    ),
    td: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <td {...rest}>{walkChildren(children, 'td')}</td>
    ),
    th: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <th {...rest}>{walkChildren(children, 'th')}</th>
    ),
    em: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <em {...rest}>{walkChildren(children, 'em')}</em>
    ),
    strong: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <strong {...rest}>{walkChildren(children, 'strong')}</strong>
    ),
    blockquote: ({ children, ...rest }: { children?: React.ReactNode }) => (
      <blockquote {...rest}>{walkChildren(children, 'bq')}</blockquote>
    ),
    a: MarkdownLink,
  };

  function Rich({ body, highlight }: RichRendererProps) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={highlight ? [rehypeHighlight] : []}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        components={components as any}
      >
        {body}
      </ReactMarkdown>
    );
  }
  return { default: Rich };
});

/**
 * Plain-text fallback used for Safari 12 / iOS 12 and as the Suspense
 * placeholder for the brief moment between mount and the markdown chunk
 * resolving on modern browsers. We still process mentions so @user /
 * @role pills render even without markdown — the mention regex itself
 * is ES5-safe, so it works fine on legacy engines.
 */
function PlainTextFallback({ body }: { body: string }) {
  // Split on blank lines so distinct paragraphs still get visible
  // separation, then per-paragraph route the text through the mention
  // walker. `whitespace-pre-wrap` preserves single newlines inside a
  // paragraph; we still want a tiny vertical gap between paragraphs.
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {paragraphs.map((para, i) => (
        <p key={`pt-${i}`} className="whitespace-pre-wrap break-words">
          {renderMentions(para, `pt-${i}`)}
        </p>
      ))}
    </div>
  );
}

export function Markdown({ body, className, emptyText, highlight = true }: MarkdownProps) {
  const trimmed = (body ?? '').trim();
  if (!trimmed) {
    return (
      <p className={cn('text-sm italic text-muted-foreground', className)}>
        {emptyText ?? 'Empty.'}
      </p>
    );
  }

  // Safari 12 / iOS 12: don't even attempt the rich renderer — that
  // would dynamic-import markdown-vendor, which crashes those engines.
  if (isLegacyRegexBrowser()) {
    return (
      <div className={cn('break-words', className)} data-markdown-root data-fallback="plain">
        <PlainTextFallback body={trimmed} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        // Typography baseline — kept compact so it sits well inside
        // dialogs, slide-overs, and cards. `max-w-none` prevents the
        // prose plugin's default 65ch cap that breaks side-by-side
        // layouts.
        'prose prose-sm max-w-none break-words dark:prose-invert',
        // Tighten headings so an h1 doesn't look like a billboard
        // inside a 320px card panel.
        'prose-headings:font-semibold prose-headings:tracking-tight',
        'prose-h1:text-lg prose-h1:mt-3 prose-h1:mb-2',
        'prose-h2:text-base prose-h2:mt-3 prose-h2:mb-2',
        'prose-h3:text-sm prose-h3:mt-2 prose-h3:mb-1',
        'prose-h4:text-sm prose-h4:mt-2 prose-h4:mb-1',
        // Lists + tables: less vertical air.
        'prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5',
        'prose-table:my-2 prose-tr:border-border prose-th:py-1 prose-td:py-1',
        // Inline code + fenced code — readable but muted vs. body.
        'prose-code:rounded prose-code:bg-muted/60 prose-code:px-1 prose-code:py-px',
        "prose-code:font-mono prose-code:text-[0.85em] prose-code:before:content-[''] prose-code:after:content-['']",
        'prose-pre:my-2 prose-pre:rounded-md prose-pre:bg-muted/40 prose-pre:p-3',
        // Blockquotes — left rail in muted foreground.
        'prose-blockquote:border-l-2 prose-blockquote:border-border prose-blockquote:pl-3',
        'prose-blockquote:not-italic prose-blockquote:text-muted-foreground',
        // Horizontal rules — subtle.
        'prose-hr:my-3 prose-hr:border-border',
        // Strong — preserve foreground colour in dark mode (avoid the
        // default washed-out white).
        'prose-strong:text-foreground',
        className,
      )}
      data-markdown-root
    >
      <React.Suspense fallback={<PlainTextFallback body={trimmed} />}>
        <RichMarkdown body={trimmed} highlight={highlight} />
      </React.Suspense>
    </div>
  );
}

export default Markdown;
